import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output, ViewEncapsulation } from '@angular/core'
import { UserReviewClient } from 'src/app/shared/services/ppv.api'
import { ToastrService } from 'ngx-toastr'
import dayjs from 'dayjs'

interface AdminMessage {
   id: number
   threadId: number
   sentByUserId: number
   sentByName: string
   message: string
   sentOn: Date
   isMine: boolean
}

interface AdminMessageThread {
   id: number
   contextType?: string
   contextId?: string
   contextLabel?: string
   contextUrl?: string
   createdByUserId: number
   createdByName: string
   createdOn: Date
   lastMessageOn: Date
   needsReply: boolean
   isUnread: boolean
   participantNames: string[]
   messages: AdminMessage[]
}

@Component({
   selector: 'app-admin-messages',
   templateUrl: './admin-messages.component.html',
   styleUrls: ['./admin-messages.component.scss'],
   standalone: false,
   encapsulation: ViewEncapsulation.None,
})
export class AdminMessagesComponent implements OnInit {
   @Output() attentionCountChange = new EventEmitter<number>()
   @Output() openContext = new EventEmitter<AdminMessageThread>()

   threads: AdminMessageThread[] = []
   loading = false
   search = ''
   filter: 'all' | 'unread' = 'all'
   expandedThreadId: number | null = null
   replyText = ''
   sending = false

   private readonly AVATAR_PALETTES = [
      { bg: '#fde5e9', fg: '#c93b55' },
      { bg: '#e0eef6', fg: '#0374b6' },
      { bg: '#daf3f2', fg: '#267f79' },
      { bg: '#fff0d7', fg: '#9a6814' },
      { bg: '#e9dee9', fg: '#831183' },
   ]

   constructor(
      private userReviewClient: UserReviewClient,
      private toastr: ToastrService,
      private cdr: ChangeDetectorRef
   ) {}

   ngOnInit(): void {
      this.loadMessages()
   }

   get attentionCount(): number {
      return this.threads.filter((t) => t.isUnread).length
   }

   get visibleThreads(): AdminMessageThread[] {
      const q = this.search.trim().toLowerCase()
      return this.threads.filter((thread) => {
         if (this.filter === 'unread' && !thread.isUnread) return false
         if (!q) return true

         const messageText = thread.messages?.map((m) => m.message).join(' ') ?? ''
         return [thread.createdByName, thread.contextLabel, ...thread.participantNames, messageText]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(q)
      })
   }

   loadMessages(): void {
      this.loading = true

      this.userReviewClient.getAdminMessages().subscribe({
         next: (data: any[]) => {
            this.threads = (data ?? []) as AdminMessageThread[]
            this.loading = false
            this.emitAttentionCount()

            if (!this.expandedThreadId && this.threads.length) {
               const first = this.threads.find((t) => t.isUnread) ?? this.threads[0]
               this.expandedThreadId = first.id

               setTimeout(() => {
                  this.markRead(first)
               }, 500)
            }

            this.cdr.detectChanges()
         },
         error: (err) => {
            this.loading = false
            console.error('Failed to load admin messages:', err)
            this.toastr.error('Could not load messages.')

            this.cdr.detectChanges()
         },
      })
   }

   setFilter(filter: 'all' | 'unread'): void {
      this.filter = filter
   }

   toggleThread(thread: AdminMessageThread): void {
      if (this.expandedThreadId === thread.id) {
         this.expandedThreadId = null
         this.replyText = ''
         return
      }

      this.expandedThreadId = thread.id
      this.replyText = ''
      this.markRead(thread)
   }

   openReply(thread: AdminMessageThread, event?: Event): void {
      event?.stopPropagation()
      this.expandedThreadId = thread.id
      this.replyText = ''
      this.markRead(thread)
   }

   openRelatedItem(thread: AdminMessageThread, event?: Event): void {
      event?.stopPropagation()

      if (this.isDeferralThread(thread)) {
         this.openContext.emit(thread)
         return
      }

      if (thread.contextUrl) {
         window.location.assign(thread.contextUrl)
      }
   }

   isDeferralThread(thread: AdminMessageThread): boolean {
      const context = `${thread.contextType ?? ''} ${thread.contextLabel ?? ''}`.toLowerCase()
      return context.includes('deferral') && !!thread.contextId
   }

   getOpenContextLabel(thread: AdminMessageThread): string {
      return this.isDeferralThread(thread) ? 'Open deferral request' : `Open ${(thread.contextType || 'related item').toLowerCase()}`
   }

   quickReply(thread: AdminMessageThread, message: string, event?: Event): void {
      event?.stopPropagation()
      this.sendReply(thread, message)
   }

   sendTypedReply(thread: AdminMessageThread): void {
      const message = this.replyText.trim()
      if (!message) return
      this.sendReply(thread, message)
   }

   private sendReply(thread: AdminMessageThread, message: string): void {
      if (this.sending) return
      this.sending = true

      this.userReviewClient.replyToAdminMessage(thread.id, { message }).subscribe({
         next: () => {
            this.sending = false
            this.replyText = ''
            this.loadMessages()
         },
         error: (err) => {
            this.sending = false
            console.error('Failed to send admin message:', err)
            this.toastr.error("Message didn't send. Try again.")
         },
      })
   }

   private markRead(thread: AdminMessageThread): void {
      if (!thread.isUnread) return

      this.userReviewClient.markAdminMessageRead(thread.id).subscribe({
         next: () => {
            thread.isUnread = false
            this.emitAttentionCount()
            this.cdr.detectChanges()
         },
         error: (err) => console.error('Failed to mark admin message read:', err),
      })
   }

   private emitAttentionCount(): void {
      this.attentionCountChange.emit(this.attentionCount)
   }

   getLastMessage(thread: AdminMessageThread): AdminMessage | null {
      return thread.messages?.length ? thread.messages[thread.messages.length - 1] : null
   }

   getSummary(thread: AdminMessageThread): string {
      const last = this.getLastMessage(thread)
      if (!last) return ''
      const prefix = last.isMine ? 'You' : last.sentByName
      return `${prefix}: ${last.message}`
   }

   getReplyBadge(thread: AdminMessageThread): string {
      const last = this.getLastMessage(thread)
      if (!last?.isMine) return 'You replied'
      const value = last.message?.trim() ?? ''
      return value.length > 0 && value.length <= 18 ? `You replied ${value}` : 'You replied'
   }

   participantLine(thread: AdminMessageThread): string {
      const names = thread.participantNames ?? []
      if (!names.length) return 'You are the only participant'
      if (names.length === 1) return `In this chat: ${names[0]} and you`
      return `In this chat: ${names.slice(0, -1).join(', ')}, ${names[names.length - 1]}, and you`
   }

   formatThreadTime(value: Date): string {
      const d = dayjs(value)
      if (d.isSame(dayjs(), 'day')) return d.format('h:mm A')
      if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Yesterday'
      return d.format('MMM D')
   }

   formatMessageTime(value: Date): string {
      return dayjs(value).format('MMM D, h:mm A')
   }

   getInitials(name: string): string {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      return parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase() : name.substring(0, 2).toUpperCase()
   }

   getAvatarBg(name: string): string {
      return this.AVATAR_PALETTES[this.hashName(name) % this.AVATAR_PALETTES.length].bg
   }

   getAvatarColor(name: string): string {
      return this.AVATAR_PALETTES[this.hashName(name) % this.AVATAR_PALETTES.length].fg
   }

   private hashName(name: string): number {
      return (name ?? '').split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)
   }
}
